// Modified from @lexical/table v0.42.0 $convertTableCellNodeElement to handle styleable table cell nodes and paddings.

import { TableCellHeaderStates } from "@lexical/table";
import {
  $createParagraphNode,
  $isInlineElementOrDecoratorNode,
  $isLineBreakNode,
  $isTextNode,
} from "lexical";

import type { StyleableColumnConfig } from "../utils/use-styleable-columns";
import type { StylableTableCellMui } from "./stylable-table-cell-mui";
import type { StylableTableCellNode } from "./stylable-table-cell-node";
import type { DOMConversionOutput, NodeKey, ParagraphNode } from "lexical";

type StylableTableCellNodeCreator = (
  styleableColumnConfig?: StyleableColumnConfig | null,
  headerState?: number,
  colSpan?: number,
  width?: number,
  key?: NodeKey,
) => StylableTableCellNode | StylableTableCellMui;

// Unfortunately it's a lot of internal logic that is not exposed in the public API.
const PIXEL_VALUE_REG_EXP = /^(\d+(?:\.\d+)?)px$/;
function isValidVerticalAlign(verticalAlign: string): boolean {
  return verticalAlign === "middle" || verticalAlign === "bottom";
}
export function $convertStylableTableCellElement(
  domNode: Node,
  styleableTableCellNodeCreator: StylableTableCellNodeCreator,
): DOMConversionOutput {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const domNode_ = domNode as HTMLTableCellElement; // includes both <td> and <th>
  const nodeName = domNode.nodeName.toLowerCase();
  let width = undefined;
  if (PIXEL_VALUE_REG_EXP.test(domNode_.style.width)) {
    width = parseFloat(domNode_.style.width);
  }
  // Determine header state based on the 'scope' attribute (matches upstream @lexical/table)
  let headerState = TableCellHeaderStates.NO_STATUS;
  if (nodeName === "th") {
    const scope = domNode_.getAttribute("scope");
    if (scope === "col") {
      headerState = TableCellHeaderStates.COLUMN;
    } else if (scope === "row") {
      headerState = TableCellHeaderStates.ROW;
    } else {
      headerState = TableCellHeaderStates.ROW;
    }
  }

  const tableCellNode = styleableTableCellNodeCreator(
    {
      column_individual_padding: true,
      column_padding_left: PIXEL_VALUE_REG_EXP.test(domNode_.style.paddingLeft)
        ? parseFloat(domNode_.style.paddingLeft)
        : 0,
      column_padding_right: PIXEL_VALUE_REG_EXP.test(domNode_.style.paddingRight)
        ? parseFloat(domNode_.style.paddingRight)
        : 0,
      column_padding_top: PIXEL_VALUE_REG_EXP.test(domNode_.style.paddingTop)
        ? parseFloat(domNode_.style.paddingTop)
        : 0,
      column_padding_bottom: PIXEL_VALUE_REG_EXP.test(domNode_.style.paddingBottom)
        ? parseFloat(domNode_.style.paddingBottom)
        : 0,
    },
    headerState,
    domNode_.colSpan,
    width,
  );

  tableCellNode.__rowSpan = domNode_.rowSpan;
  const { backgroundColor } = domNode_.style;
  if (backgroundColor !== "") {
    tableCellNode.__backgroundColor = backgroundColor;
  }
  const { verticalAlign } = domNode_.style;
  if (isValidVerticalAlign(verticalAlign)) {
    tableCellNode.__verticalAlign = verticalAlign;
  }
  const { style } = domNode_;
  const textDecoration = ((style && style.textDecoration) || "").split(" ");
  const hasBoldFontWeight = style.fontWeight === "700" || style.fontWeight === "bold";
  const hasLinethroughTextDecoration = textDecoration.includes("line-through");
  const hasItalicFontStyle = style.fontStyle === "italic";
  const hasUnderlineTextDecoration = textDecoration.includes("underline");
  return {
    after: (childLexicalNodes) => {
      const result = [];
      let paragraphNode: ParagraphNode | null = null;
      const removeSingleLineBreakNode = (): void => {
        if (paragraphNode) {
          const firstChild = paragraphNode.getFirstChild();
          if ($isLineBreakNode(firstChild) && paragraphNode.getChildrenSize() === 1) {
            firstChild.remove();
          }
        }
      };
      for (const child of childLexicalNodes) {
        if (
          $isInlineElementOrDecoratorNode(child) ||
          $isTextNode(child) ||
          $isLineBreakNode(child)
        ) {
          if ($isTextNode(child)) {
            if (hasBoldFontWeight) {
              child.toggleFormat("bold");
            }
            if (hasLinethroughTextDecoration) {
              child.toggleFormat("strikethrough");
            }
            if (hasItalicFontStyle) {
              child.toggleFormat("italic");
            }
            if (hasUnderlineTextDecoration) {
              child.toggleFormat("underline");
            }
          }
          if (paragraphNode) {
            paragraphNode.append(child);
          } else {
            paragraphNode = $createParagraphNode().append(child);
            result.push(paragraphNode);
          }
        } else {
          result.push(child);
          removeSingleLineBreakNode();
          paragraphNode = null;
        }
      }
      removeSingleLineBreakNode();
      if (result.length === 0) {
        result.push($createParagraphNode());
      }
      return result;
    },
    node: tableCellNode,
  };
}
